import React, { useEffect, useMemo, useRef, useState } from "react";
import { openPaymentGateway } from "../../services/cyberGatewayService";
import { checkBookingPaymentStatus } from "../../services/bookingService";

// "Pay Now" from a booking's details page. The backend only ever returns a
// booking's gateway payment_payload in the POST /bookings response — neither
// GET /member/bookings/{id} nor POST /booking-payments/status carries it —
// so the create response is remembered here (sessionStorage, per tab, same
// as the auth tokens) for the length of its lock. Past that the backend
// expires the pending payment anyway, so there's nothing left to resume.
const RESUMABLE_PAYMENTS_KEY = "sfmis_resumable_booking_payments";

const readResumablePayments = () => {
  try {
    return JSON.parse(sessionStorage.getItem(RESUMABLE_PAYMENTS_KEY)) || {};
  } catch {
    return {};
  }
};

const writeResumablePayments = (map) => {
  try {
    sessionStorage.setItem(RESUMABLE_PAYMENTS_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable (private mode, quota) — resuming just won't be offered.
  }
};

// Drops entries whose lock has already lapsed so the map never grows unbounded.
const liveResumablePayments = () =>
  Object.fromEntries(Object.entries(readResumablePayments()).filter(([, v]) => v?.lockDeadline > Date.now()));

export const rememberBookingPayment = (createResponse) => {
  const bookingId = createResponse?.booking?.id ?? createResponse?.booking_id;
  if (bookingId == null || !createResponse?.payment_payload) return;
  const lockMinutes = createResponse.lock_expires_in_minutes ?? 10;
  writeResumablePayments({
    ...liveResumablePayments(),
    [bookingId]: { response: createResponse, lockDeadline: Date.now() + lockMinutes * 60 * 1000 },
  });
};

// Returns { response, lockDeadline } for a booking still within its hold, or null.
export const getResumableBookingPayment = (bookingId) => liveResumablePayments()[bookingId] || null;

export const forgetBookingPayment = (bookingId) => {
  const map = liveResumablePayments();
  delete map[bookingId];
  writeResumablePayments(map);
};

// "Pay for your booking" — opened immediately after POST /bookings locks the
// selected slots for 10 minutes. Mirrors PaymentMethod.jsx/
// IdCardReprintPaymentModal.jsx's gateway-popup + polling UX, but:
//   - Card payment only — BookingController::manualConfirm rejects
//     shroff/manual payment for bookings outright, so there's no counter
//     option to offer.
//   - Adds a live lock countdown, since bookings are the only payment flow
//     in this app where the underlying record actually expires out from
//     under the user. The countdown runs from mount (the lock started the
//     moment POST /bookings returned), independent of whether the gateway
//     popup has been opened yet.
//   - Polls the real dedicated POST /booking-payments/status route (not a
//     workaround like the reprint modal's re-GET), echoing back the exact
//     payment_reference + booking_id the create response returned.
//
// Props: `booking` (the full POST /bookings response — message,
// lock_expires_in_minutes, payment_reference, payable_amount,
// payment_payload, booking:{id,...}), `onClose()`, `onPaid(statusResponse)`,
// `onExpired()` — called once the lock is confirmed expired (via the
// backend, not just the local countdown reaching zero) so the caller can
// send the user back to slot selection rather than leave a dead retry button.
// `lockDeadline` (optional, epoch ms) — when resuming a payment from
// getResumableBookingPayment() below, the countdown continues from the
// original hold instead of restarting the full lock_expires_in_minutes.
export default function BookingPaymentModal({ booking, onClose, onPaid, onExpired, lockDeadline: resumeDeadline }) {
  const PRIMARY = "#1d4ed8";
  const NAVY = "#0f1c3f";
  const LIGHT = "#e8f0fe";

  const bookingId = booking?.booking?.id;
  const entryCount = booking?.booking?.entries?.length ?? 0;
  const paymentReference = booking?.payment_reference || booking?.external_payment_reference;
  const paymentPayload = booking?.payment_payload || null;
  const amount = booking?.payable_amount != null ? Number(booking.payable_amount) : null;
  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";
  const lockMinutes = booking?.lock_expires_in_minutes ?? 10;
  const lockDeadline = useMemo(
    () => resumeDeadline ?? Date.now() + lockMinutes * 60 * 1000,
    [resumeDeadline, lockMinutes]
  );

  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [error, setError] = useState("");
  const popupRef = useRef(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [paidResult, setPaidResult] = useState(null);
  const [expired, setExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.round((lockDeadline - Date.now()) / 1000)));

  const checkStatus = async () => {
    const res = await checkBookingPaymentStatus({ payment_reference: paymentReference, booking_id: bookingId });
    return res;
  };

  const verifyPayment = async () => {
    setVerifying(true);
    setPaymentNotice("");
    try {
      const res = await checkStatus();
      if (res?.payment_completed) {
        setPaidResult(res);
        onPaid?.(res);
        return;
      }
      if (res?.payment_status && res.payment_status !== "Pending") {
        // Backend has confirmed the lock expired (status flipped away from
        // Pending without ever completing) — no point offering a retry.
        setExpired(true);
        onExpired?.();
        return;
      }
      setPaymentNotice(
        res?.payment_status
          ? `Payment status: ${res.payment_status}. If you completed the payment, please wait a moment and check again.`
          : "Payment is still pending. If you completed the payment, please wait a moment and check again."
      );
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  // Live lock countdown — ticks every second regardless of whether the
  // gateway popup has been opened. Once it hits zero, do one authoritative
  // check with the backend (which is what actually flips the lock to
  // Expired) rather than trusting the local clock alone.
  useEffect(() => {
    if (paidResult || expired) return undefined;
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.round((lockDeadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        checkStatus()
          .then((res) => {
            if (res?.payment_completed) {
              setPaidResult(res);
              onPaid?.(res);
            } else {
              setExpired(true);
              onExpired?.();
            }
          })
          .catch(() => {
            setExpired(true);
            onExpired?.();
          });
      }
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockDeadline, paidResult, expired]);

  // While the gateway popup is open, poll for the real payment status every
  // few seconds — same shape as PaymentMethod.jsx's polling effect.
  useEffect(() => {
    if (!gatewayOpened) return undefined;
    const popup = popupRef.current;
    if (!popup) return undefined;

    let stopped = false;
    let inFlight = false;

    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };

    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        const res = await checkStatus();
        if (stopped) return;
        if (res?.payment_completed) {
          stopPolling();
          popup.close();
          setPaidResult(res);
          onPaid?.(res);
        } else if (res?.payment_status && res.payment_status !== "Pending") {
          stopPolling();
          popup.close();
          setExpired(true);
          onExpired?.();
        }
      } catch {
        // Transient error — keep polling.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(tick, 3000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handleContinue = () => {
    setError("");
    setPaymentNotice("");

    if (!paymentPayload) {
      setError("Missing payment details. Please go back and try again.");
      return;
    }

    const popup = openPaymentGateway(paymentPayload);
    if (!popup) {
      popupRef.current = null;
      setError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setGatewayOpened((n) => n + 1);
  };

  const confirmingPayment = gatewayOpened > 0 && !paymentNotice && !paidResult && !expired;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  if (paidResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>Booking Confirmed</h2>
          <p className="text-sm text-gray-500 mt-1">Your facility booking is paid and confirmed.</p>
          <button
            onClick={onClose}
            className="w-full mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
            style={{ backgroundColor: PRIMARY }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>Booking Lock Expired</h2>
          <p className="text-sm text-gray-500 mt-1">
            The {lockMinutes}-minute hold on your selected time slot{entryCount > 1 ? "s" : ""} has expired. Please
            select your time slot{entryCount > 1 ? "s" : ""} again.
          </p>
          <button
            onClick={onClose}
            className="w-full mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
            style={{ backgroundColor: PRIMARY }}
          >
            Back to Booking
          </button>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="flex flex-col items-center gap-4 text-center max-w-xs">
          <svg className="animate-spin w-10 h-10 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-white text-base font-semibold">Verifying your payment…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-lg font-bold mb-1 pr-8" style={{ color: NAVY }}>Confirm Your Payment</h2>
        <p className="text-sm text-gray-500 mb-1">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>
        {entryCount > 1 && <p className="text-xs text-gray-500 mb-3">{entryCount} time slots in this booking</p>}

        <div
          className="rounded-xl px-4 py-3 mb-5 flex items-center justify-between gap-3"
          style={{ backgroundColor: secondsLeft <= 60 ? "#fee2e2" : LIGHT }}
        >
          <span className="text-xs font-semibold" style={{ color: secondsLeft <= 60 ? "#b91c1c" : NAVY }}>
            Slot held for
          </span>
          <span className="text-lg font-extrabold font-mono" style={{ color: secondsLeft <= 60 ? "#b91c1c" : PRIMARY }}>
            {mm}:{ss}
          </span>
        </div>

        <div className="rounded-xl border-2 px-4 py-3.5" style={{ borderColor: PRIMARY, backgroundColor: `${PRIMARY}0d` }}>
          <p className="text-sm font-bold text-gray-900">Card Payment</p>
          <p className="text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</p>
        </div>

        {error && <p className="text-sm text-red-500 text-center mt-4">{error}</p>}
        {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

        <div className="border-t border-gray-100 mt-5 pt-5">
          <button
            onClick={handleContinue}
            disabled={confirmingPayment}
            className="w-full text-white font-semibold py-2.5 rounded-xl text-sm transition disabled:opacity-70"
            style={{ backgroundColor: PRIMARY }}
          >
            {confirmingPayment ? "Confirming Payment…" : gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
          </button>
          {confirmingPayment && (
            <p className="text-xs font-medium text-amber-600 text-center mt-3">Confirming your payment... please don't close this.</p>
          )}
          {paymentNotice && (
            <button
              onClick={verifyPayment}
              className="w-full text-sm font-medium border rounded-xl py-2 mt-3 transition"
              style={{ color: PRIMARY, borderColor: LIGHT }}
            >
              Check Payment Status Again
            </button>
          )}
          <button onClick={onClose} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
